import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/Header";
import Quiz from "./pages/Quiz";
import Questions from "@/components/Questions";
import Home from './pages/Home';
import TestMode from './pages/TestMode';
import SubjectSelection from './pages/SubjectSelection';
import Admin from './pages/Admin';
import AdminLogin from './pages/AdminLogin';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from "@/contexts/AuthContext";
import { useState } from "react";
import { setUserPremium, type PaymentTransaction } from "@/lib/firebase";
import { PaywallModal } from "@/components/PaywallModal";
import { HelmetProvider } from 'react-helmet-async';

const queryClient = new QueryClient();

const AppContent = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  // For non-admin routes
  if (!isAdminRoute) {
    const { user } = useAuth();
    const [showPaywall, setShowPaywall] = useState(false);

    const handlePaymentSuccess = async (response: any) => {
      if (!user) return;

      const transaction: PaymentTransaction = {
        reference: response.reference,
        amount: response.amount / 100,
        timestamp: new Date(),
        status: 'success',
        userEmail: user.email || ''
      };

      await setUserPremium(user.uid, transaction);
      setShowPaywall(false);
    };

    return (
      <>
        {user && <Header />}
        <main className={user ? "pt-16" : ""}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/test-mode" element={<TestMode />} />
            <Route path="/subjects" element={<SubjectSelection />} />
            <Route path="/quiz" element={<Quiz />} />
            <Route path="/questions" element={<Questions />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
        
        <PaywallModal
          isOpen={showPaywall}
          onClose={() => setShowPaywall(false)}
          onPaymentSuccess={handlePaymentSuccess}
          userEmail={user?.email || ''}
        />
      </>
    );
  }

  // For admin routes
  return (
    <Routes>
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
      <Route 
        path="/admin/dashboard" 
        element={
          <ProtectedRoute>
            <Admin />
          </ProtectedRoute>
        } 
      />
      <Route path="*" element={<Navigate to="/admin/login" replace />} />
    </Routes>
  );
};

const App = () => {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <Router>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <AuthProvider>
              <AppContent />
            </AuthProvider>
          </TooltipProvider>
        </Router>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
